import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import { Autocomplete, Box, Divider, InputAdornment, TextField, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import { apiZodios } from '~api/apiZodios'
import { LocationIcon, SearchIcon } from '~assets/icons'
import { Button } from '~components/common'
import { MAPBOX_TOKEN } from '~config'
import { RootState } from '~redux/rootReducer'
import { GeoPoint } from '~types'

export interface ILocationEntryInlineAddress {
  address: string
  location: GeoPoint
}

interface LocationEntryInlineProps {
  onSave: (inputAddress: ILocationEntryInlineAddress) => void
  onCancel: () => void
  initialPosition: GeoPoint
}

interface IMapboxContextItem {
  id: string
  text: string
}

interface IMapboxFeature {
  text: string
  context?: IMapboxContextItem[]
}

interface IMapboxGeocodeResponse {
  features: IMapboxFeature[]
}

const LocationEntryInline: React.FC<LocationEntryInlineProps> = ({
  onSave,
  onCancel,
  initialPosition,
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const marker = useRef<mapboxgl.Marker | null>(null)

  const [addressInput, setAddressInput] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [open, setOpen] = useState(false)

  const [markerPosition, setMarkerPosition] = useState<GeoPoint>(initialPosition)
  const [addressFields, setAddressFields] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  })
  const [isMapInitialized, setIsMapInitialized] = useState(false)

  const searchAddress = debounce(async (value: string) => {
    if (!user || value.length < 3) {
      setSuggestions([])
      setIsSearching(false)
      setOpen(false)
      return
    }

    setIsSearching(true)
    try {
      const { addresses: resultsAddresses } = await apiZodios.contacts_searchAddresses({
        user_id: user.id,
        address: value,
      })
      setSuggestions(resultsAddresses.slice(0, 5))
      setOpen(resultsAddresses.length > 0)
    } catch (error) {
      console.error('Error searching for address:', error)
      setSuggestions([])
      setOpen(false)
    } finally {
      setIsSearching(false)
    }
  }, 300)

  const handleAddressSelect = async (address: string | null): Promise<void> => {
    if (!user || !address) return

    try {
      const response = await apiZodios.contacts_quickCreateAddress({
        user_id: user.id,
        address: address,
      })

      if (response.address) {
        const addr = response.address
        setAddressFields({
          street: addr.street,
          city: addr.city,
          state: addr.state,
          zip: addr.zip,
        })

        if (addr.location) {
          setMarkerPosition(addr.location)
          updateMapLocation(addr.location)
        }
        setAddressInput(address)
      }
    } catch (error) {
      console.error('Error creating quick address:', error)
    }
  }

  // Parse coordinates from text input (e.g., "lat,lon" or "lat, lon")
  const parseCoordinates = (input: string): GeoPoint | null => {
    const coordPattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/
    const match = input.trim().match(coordPattern)

    if (match) {
      const lat = parseFloat(match[1])
      const lon = parseFloat(match[2])

      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { lat, lon }
      }
    }
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (_: any, value: string, reason: string): void => {
    setAddressInput(value)

    // Check if input looks like coordinates
    const coords = parseCoordinates(value)
    if (coords) {
      setMarkerPosition(coords)
      updateMapLocation(coords)
      reverseGeocode(coords)
      setSuggestions([]) // Clear suggestions for coordinate input
      setOpen(false)
    } else if (reason === 'input') {
      // Only search when user is actually typing, not when selecting
      searchAddress(value)
    }
  }

  // Reverse geocode coordinates to get address
  const reverseGeocode = async (position: GeoPoint): Promise<void> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${position.lon},${position.lat}.json?access_token=${MAPBOX_TOKEN}`,
      )
      const data: IMapboxGeocodeResponse = await response.json()

      if (data.features && data.features.length > 0) {
        const place = data.features[0]
        setAddressFields({
          street: place.text || '',
          city:
            place.context?.find((c: IMapboxContextItem) => c.id.startsWith('place.'))?.text || '',
          state:
            place.context?.find((c: IMapboxContextItem) => c.id.startsWith('region.'))?.text || '',
          zip:
            place.context?.find((c: IMapboxContextItem) => c.id.startsWith('postcode.'))?.text ||
            '',
        })
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error)
    }
  }

  const updateMapLocation = (position: GeoPoint): void => {
    if (map.current && marker.current) {
      map.current.setCenter([position.lon, position.lat])
      marker.current.setLngLat([position.lon, position.lat])
    }
  }

  const handleSave = (): void => {
    onSave({
      address: displayAddress,
      location: markerPosition,
    })
  }

  const handleUseCurrentLocation = (): void => {
    if (navigator.geolocation) {
      setIsGettingLocation(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const newPosition = { lat: latitude, lon: longitude }
          setMarkerPosition(newPosition)
          updateMapLocation(newPosition)
          reverseGeocode(newPosition)
          setIsGettingLocation(false)
        },
        (error) => {
          console.error('Error getting location:', error)
          setIsGettingLocation(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        },
      )
    } else {
      console.error('Geolocation is not supported by your browser.')
    }
  }

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || isMapInitialized) return

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [markerPosition.lon, markerPosition.lat],
      zoom: 13,
      accessToken: MAPBOX_TOKEN,
    })

    marker.current = new mapboxgl.Marker({
      draggable: true,
      color: '#1976d2',
    })
      .setLngLat([markerPosition.lon, markerPosition.lat])
      .addTo(map.current)

    // Handle marker drag
    marker.current.on('dragend', () => {
      if (marker.current) {
        const lngLat = marker.current.getLngLat()
        const newPosition = { lon: lngLat.lng, lat: lngLat.lat }
        setMarkerPosition(newPosition)
        reverseGeocode(newPosition)
      }
    })

    map.current.on('load', () => {
      map.current?.resize()
      setIsMapInitialized(true)
    })

    return (): void => {
      if (marker.current) marker.current.remove()
      if (map.current) map.current.remove()
      setIsMapInitialized(false)
    }
  }, [])

  // Update marker position when markerPosition changes
  useEffect(() => {
    if (marker.current && isMapInitialized) {
      marker.current.setLngLat([markerPosition.lon, markerPosition.lat])
      if (map.current) {
        map.current.setCenter([markerPosition.lon, markerPosition.lat])
      }
    }
  }, [markerPosition, isMapInitialized])

  const isValidLocation = markerPosition.lat !== 0 || markerPosition.lon !== 0
  const displayAddress = addressFields.street
    ? `${addressFields.street}, ${addressFields.city}, ${addressFields.state} ${addressFields.zip}`.trim()
    : addressInput

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '500px',
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Typography variant='h6' sx={{ fontSize: '16px', fontWeight: 600 }}>
          Provide Your Location
        </Typography>
      </Box>

      <Divider />

      {/* Input Section */}
      <Box sx={{ p: 2 }}>
        <Autocomplete
          freeSolo
          options={suggestions}
          value={addressInput}
          inputValue={addressInput}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onInputChange={handleInputChange}
          onChange={(_, value) => {
            if (value && typeof value === 'string') {
              handleAddressSelect(value)
              setOpen(false)
            }
          }}
          loading={isSearching}
          filterOptions={(options) => options} // Don't filter, use our custom options
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder='Enter address or coordinates (e.g., 40.7128, -74.0060)'
              variant='outlined'
              size='small'
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Use Current Location Button */}
        <Button
          variant='outlined'
          color='secondary'
          startIcon={<MyLocationIcon aria-hidden='true' />}
          onClick={handleUseCurrentLocation}
          disabled={isGettingLocation}
          fullWidth
          sx={{
            mb: 2,
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              marginRight: '8px',
            },
            fontSize: '0.875rem', // Slightly smaller font to fit better
          }}
        >
          {isGettingLocation ? 'Getting Location...' : 'Use Current Location'}
        </Button>

        {/* Current Location Display */}
        {isValidLocation && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Selected Location:
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {displayAddress}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              fontFamily='monospace'
              sx={{ mt: 0.5 }}
            >
              {`<${markerPosition.lat.toFixed(6)}, ${markerPosition.lon.toFixed(6)}>`}
            </Typography>
          </Box>
        )}

        {/* Map */}
        <Box
          ref={mapContainer}
          sx={{
            height: '200px',
            width: '100%',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />

        <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
          Drag the marker to adjust your exact location
        </Typography>
      </Box>

      <Divider />

      {/* Action Buttons */}
      <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button variant='outlined' size='small' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button variant='contained' size='small' onClick={handleSave} disabled={!isValidLocation}>
          Save
        </Button>
      </Box>
    </Box>
  )
}

export default LocationEntryInline
